/**
 * @file DynamicFormLauncher.tsx
 * @description Modal-based dynamic form renderer powered by the Form Builder.
 *
 * Resolves a form schema via the entity form resolution API, renders fields
 * dynamically using DynamicFormField, runs validation & conditional logic,
 * and delegates submission to the caller-provided onSubmit handler.
 *
 * This component is rendered once in the app shell and controlled via
 * DynamicFormContext. Usage sites call `openDynamicForm()` with:
 *   - entityType + programId → resolves the form schema
 *   - onSubmit(formData)     → caller handles domain-specific API
 *   - prefill                → initial field values
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save, AlertCircle } from 'lucide-react';

import {
  AppModal,
  AppModalHeader,
  AppModalTitle,
  AppModalDescription,
  AppModalBody,
  AppModalFooter,
} from '@/modules/shared/components/ui/AppModal';
import { AppButtonLoader, AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { Button } from '@/modules/shared/components/ui/button';
import { useToast } from '@/modules/shared/components/ui/toast';

import { useDynamicForm } from '../../contexts/DynamicFormContext';
import { useEntityFormResolution } from '../../hooks/useEntityFormResolution';
import { DynamicFormField } from '../workspace/DynamicFormField';
import {
  getFieldDefaultValue,
  type FieldValue,
} from '../workspace/dynamicFormFieldUtils';
import type { FormFieldDefinition } from '../../types/formBuilder';
import {
  validateAllFields,
  evaluateComputedFields,
} from '../workspace/formRuntimeEngine';
import { evaluateRules, type FormRule, type RuleEvaluationResult } from '../../types/formRuleEngine';
import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import {
  evaluateUiVisibility,
  type UiRuleContext,
  type UiVisibilityResult,
} from '../../../shared/utils/uiVisibilityEngine';

// ── Empty defaults ──────────────────────────────────────────────────────────

const EMPTY_RULE_RESULT: RuleEvaluationResult = {
  showFields: new Set(),
  hideFields: new Set(),
  setValues: {},
  readOnlyFields: new Set(),
  disabledFields: new Set(),
  enabledFields: new Set(),
  requiredOverrides: {},
  blockMessages: [],
  triggeredWorkflows: [],
  assignedRoles: [],
  alerts: [],
  tasks: [],
  notifications: [],
  moveToSteps: [],
  showSections: new Set(),
  hideSections: new Set(),
  showTabs: new Set(),
  hideTabs: new Set(),
  showColumns: new Set(),
  hideColumns: new Set(),
  showZones: new Set(),
  hideZones: new Set(),
};

const EMPTY_UI_VISIBILITY: UiVisibilityResult = {
  hiddenFieldKeys: new Set(),
  disabledFieldKeys: new Set(),
  hiddenSectionKeys: new Set(),
  hiddenTabKeys: new Set(),
  hiddenColumnKeys: new Set(),
  hiddenZoneKeys: new Set(),
  hiddenActionKeys: new Set(),
  readOnlyFieldKeys: new Set(),
};

// ── Component ───────────────────────────────────────────────────────────────

export function DynamicFormLauncher() {
  const { isOpen, activeRequest, closeDynamicForm } = useDynamicForm();
  const { showToast } = useToast();

  // Internal state
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initRef = useRef(false);

  // ── Form Resolution ─────────────────────────────────────────────────────

  const {
    formSchema,
    resolution,
    isLoading: isFormLoading,
    error: formResolutionError,
    noFormAvailable,
  } = useEntityFormResolution({
    programId: activeRequest?.programId,
    entityType: activeRequest?.entityType ?? 'HOSPITAL',
    actionType: activeRequest?.actionType,
    workflowStepId: activeRequest?.workflowStepId,
    contextType: activeRequest?.contextType ?? 'MODAL',
    enabled: isOpen && !!activeRequest?.programId,
  });

  // ── Initialize form data from schema defaults + prefill ─────────────────

  useEffect(() => {
    if (!formSchema?.fields || !isOpen) return;
    if (initRef.current) return;

    const defaults: Record<string, FieldValue> = {};
    for (const field of formSchema.fields) {
      defaults[field.internalName] = getFieldDefaultValue(field);
    }

    // Overlay prefill values
    if (activeRequest?.prefill) {
      for (const [key, val] of Object.entries(activeRequest.prefill)) {
        defaults[key] = val;
      }
    }

    setFormData(defaults);
    setErrors({});
    initRef.current = true;
  }, [formSchema?.fields, isOpen, activeRequest?.prefill]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({});
      setErrors({});
      setIsSubmitting(false);
      initRef.current = false;
    }
  }, [isOpen]);

  // ── Conditional Logic Engine ────────────────────────────────────────────

  const conditionalLogic = useConditionalLogic(formSchema?.fields, formData);

  // ── Business Rule Engine ────────────────────────────────────────────────

  const formRules = useMemo<FormRule[]>(() => {
    if (formSchema?.rules && formSchema.rules.length > 0) return formSchema.rules;
    try {
      const raw = formSchema?.properties?.formRules;
      return raw ? JSON.parse(String(raw)) : [];
    } catch { return []; }
  }, [formSchema]);

  const ruleResult = useMemo<RuleEvaluationResult>(() => {
    if (formRules.length === 0) return EMPTY_RULE_RESULT;
    const onLoadResult = evaluateRules(formRules, formData, 'ON_LOAD');
    const onChangeResult = evaluateRules(formRules, formData, 'ON_CHANGE');
    return {
      showFields: new Set([...onLoadResult.showFields, ...onChangeResult.showFields]),
      hideFields: new Set([...onLoadResult.hideFields, ...onChangeResult.hideFields]),
      setValues: { ...onLoadResult.setValues, ...onChangeResult.setValues },
      readOnlyFields: new Set([...onLoadResult.readOnlyFields, ...onChangeResult.readOnlyFields]),
      disabledFields: new Set([...onLoadResult.disabledFields, ...onChangeResult.disabledFields]),
      enabledFields: new Set([...onLoadResult.enabledFields, ...onChangeResult.enabledFields]),
      requiredOverrides: { ...onLoadResult.requiredOverrides, ...onChangeResult.requiredOverrides },
      blockMessages: [...onLoadResult.blockMessages, ...onChangeResult.blockMessages],
      triggeredWorkflows: [...onLoadResult.triggeredWorkflows, ...onChangeResult.triggeredWorkflows],
      assignedRoles: [...onLoadResult.assignedRoles, ...onChangeResult.assignedRoles],
      alerts: [...onLoadResult.alerts, ...onChangeResult.alerts],
      tasks: [...onLoadResult.tasks, ...onChangeResult.tasks],
      notifications: [...onLoadResult.notifications, ...onChangeResult.notifications],
      moveToSteps: [...onLoadResult.moveToSteps, ...onChangeResult.moveToSteps],
      showSections: new Set([...onLoadResult.showSections, ...onChangeResult.showSections]),
      hideSections: new Set([...onLoadResult.hideSections, ...onChangeResult.hideSections]),
      showTabs: new Set([...onLoadResult.showTabs, ...onChangeResult.showTabs]),
      hideTabs: new Set([...onLoadResult.hideTabs, ...onChangeResult.hideTabs]),
      showColumns: new Set([...onLoadResult.showColumns, ...onChangeResult.showColumns]),
      hideColumns: new Set([...onLoadResult.hideColumns, ...onChangeResult.hideColumns]),
      showZones: new Set([...onLoadResult.showZones, ...onChangeResult.showZones]),
      hideZones: new Set([...onLoadResult.hideZones, ...onChangeResult.hideZones]),
    };
  }, [formRules, formData]);

  // ── UI Visibility Engine ────────────────────────────────────────────────

  const uiVisibilityRules = useMemo(
    () => formSchema?.uiVisibilityRules ?? [],
    [formSchema?.uiVisibilityRules],
  );

  const uiVisibility = useMemo<UiVisibilityResult>(() => {
    if (uiVisibilityRules.length === 0) return EMPTY_UI_VISIBILITY;
    const ctx: UiRuleContext = {
      entityType: activeRequest?.entityType ?? '',
      formData,
      programId: activeRequest?.programId ?? '',
      roleIds: [],
    };
    return evaluateUiVisibility(uiVisibilityRules, ctx);
  }, [uiVisibilityRules, formData, activeRequest?.entityType, activeRequest?.programId]);

  // ── Computed Fields ─────────────────────────────────────────────────────

  const computedValues = useMemo(
    () => formSchema?.fields ? evaluateComputedFields(formSchema.fields, formData) : formData,
    [formSchema?.fields, formData],
  );

  // ── Field Map (for FIELD_GROUP children) ────────────────────────────────

  const fieldMap = useMemo(() => {
    const map = new Map<string, FormFieldDefinition>();
    for (const f of formSchema?.fields ?? []) {
      map.set(f.fieldId, f);
      map.set(f.internalName, f);
    }
    return map;
  }, [formSchema?.fields]);

  const dependencyFieldKeyMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of formSchema?.fields ?? []) {
      map[f.fieldId] = f.internalName;
      map[f.internalName] = f.internalName;
    }
    return map;
  }, [formSchema?.fields]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleFieldChange = useCallback((fieldName: string, value: FieldValue) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => { const next = { ...prev }; delete next[fieldName]; return next; });
    }
  }, [errors]);

  const validate = (): boolean => {
    if (!formSchema?.fields) return false;
    const newErrors = validateAllFields(
      formSchema.fields,
      formData,
      conditionalLogic.visibility,
      conditionalLogic.requiredOverrides,
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!activeRequest || !validate()) return;

    setIsSubmitting(true);
    try {
      await activeRequest.onSubmit(formData);
      showToast('Submitted successfully.', 'success');
      activeRequest.onSuccess?.();
      closeDynamicForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      showToast(message, 'error');
      activeRequest.onError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    closeDynamicForm();
  };

  // ── Visible fields (filtered by conditional logic + rule engine + UI visibility) ──

  const visibleFields = useMemo(() => {
    if (!formSchema?.fields) return [];
    return formSchema.fields.filter((field) => {
      // Filter out child fields that belong to a FIELD_GROUP (rendered inside the group)
      if (field.properties?.parentGroupId) return false;
      // Conditional logic
      if (!conditionalLogic.isVisible(field.fieldId)) return false;
      // Rule engine hide
      if (ruleResult.hideFields.has(field.internalName) && !ruleResult.showFields.has(field.internalName)) return false;
      // UI visibility engine
      if (uiVisibility.hiddenFieldKeys.has(field.internalName)) return false;
      return true;
    });
  }, [formSchema?.fields, conditionalLogic, ruleResult, uiVisibility]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!isOpen || !activeRequest) return null;

  const title = activeRequest.title ?? `${activeRequest.actionType === 'EDIT' ? 'Edit' : 'New'} ${activeRequest.entityType}`;
  const subtitle = activeRequest.subtitle ?? resolution?.formDisplayName ?? resolution?.formName;

  return (
    <AppModal
      open={isOpen}
      onClose={handleClose}
      size={activeRequest.size ?? 'lg'}
      preventBackdropClose={isSubmitting}
    >
      <AppModalHeader>
        <AppModalTitle>{title}</AppModalTitle>
        {subtitle && <AppModalDescription>{subtitle}</AppModalDescription>}
      </AppModalHeader>

      <AppModalBody>
        {/* Loading state */}
        {isFormLoading && (
          <div className="flex items-center justify-center py-8">
            <AppInlineLoader label="Loading content" />
          </div>
        )}

        {/* Error state */}
        {(formResolutionError || noFormAvailable) && !isFormLoading && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span>
              {noFormAvailable
                ? `No published form found for ${activeRequest.entityType}. Please create and publish a form in Form Builder, then configure the mapping.`
                : `Failed to load form: ${formResolutionError instanceof Error ? formResolutionError.message : 'Unknown error'}`}
            </span>
          </div>
        )}

        {/* Form fields */}
        {formSchema && !isFormLoading && (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            className="grid grid-cols-12 gap-3"
          >
            {visibleFields.map((field) => {
              // Rule engine value overrides
              const ruleSetValue = ruleResult.setValues[field.internalName];
              const forcedVal = ruleSetValue !== undefined
                ? ruleSetValue
                : conditionalLogic.forcedValues[field.internalName];
              const effectiveValue = forcedVal !== undefined
                ? forcedVal
                : field.fieldType === 'COMPUTED'
                  ? computedValues[field.internalName]
                  : (formData[field.internalName] ?? getFieldDefaultValue(field));

              // Disabled state
              const isConditionallyDisabled = !conditionalLogic.isEnabled(field.fieldId)
                || ruleResult.readOnlyFields.has(field.internalName)
                || ruleResult.disabledFields.has(field.internalName)
                || uiVisibility.disabledFieldKeys.has(field.internalName)
                || uiVisibility.readOnlyFieldKeys.has(field.internalName);

              // Required state
              const ruleRequiredOverride = ruleResult.requiredOverrides[field.internalName];
              const isConditionallyRequired = ruleRequiredOverride !== undefined
                ? ruleRequiredOverride
                : conditionalLogic.isRequired(field);
              const effectiveField = isConditionallyRequired !== !!field.mandatory
                ? { ...field, mandatory: isConditionallyRequired }
                : field;

              // FIELD_GROUP rendering
              if (field.fieldType === 'FIELD_GROUP') {
                const childIds = typeof field.properties?.childFieldIds === 'string'
                  ? (field.properties.childFieldIds as string).split(',').map((s) => s.trim()).filter(Boolean)
                  : [];
                const childFields = childIds
                  .map((id) => fieldMap.get(id))
                  .filter((f): f is NonNullable<typeof f> => !!f);

                return (
                  <div key={field.fieldId} className="col-span-12 rounded-lg border border-border p-3 space-y-2 bg-muted/10">
                    <label className="text-xs font-semibold">{field.label}</label>
                    {field.helpText && (
                      <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
                    )}
                    <div className="grid grid-cols-12 gap-3">
                      {childFields
                        .filter((cf) => conditionalLogic.isVisible(cf.fieldId))
                        .map((childField) => (
                          <DynamicFormField
                            key={childField.fieldId}
                            field={conditionalLogic.isRequired(childField) !== !!childField.mandatory
                              ? { ...childField, mandatory: conditionalLogic.isRequired(childField) }
                              : childField}
                            value={formData[childField.internalName] ?? getFieldDefaultValue(childField)}
                            onChange={handleFieldChange}
                            error={errors[childField.internalName]}
                            disabled={isSubmitting || !conditionalLogic.isEnabled(childField.fieldId)}
                            computedValue={computedValues[childField.internalName]}
                            formValues={formData}
                            dependencyFieldKeyMap={dependencyFieldKeyMap}
                          />
                        ))}
                    </div>
                  </div>
                );
              }

              return (
                <DynamicFormField
                  key={field.fieldId}
                  field={effectiveField}
                  value={effectiveValue}
                  onChange={handleFieldChange}
                  error={errors[field.internalName]}
                  disabled={isSubmitting || isConditionallyDisabled}
                  computedValue={computedValues[field.internalName]}
                  formValues={formData}
                  dependencyFieldKeyMap={dependencyFieldKeyMap}
                />
              );
            })}
          </form>
        )}
      </AppModalBody>

      <AppModalFooter>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClose}
          disabled={isSubmitting}
          className="h-8 text-xs"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || isFormLoading || noFormAvailable || !!formResolutionError}
          className="h-8 text-xs gap-1"
        >
          {isSubmitting ? (
            <>
              <AppButtonLoader compact />
              Submitting...
            </>
          ) : (
            <>
              <Save className="w-3 h-3" />
              Submit
            </>
          )}
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}
